import { SuiClient } from "@mysten/sui/client";
import { TransactionObjectArgument } from "@mysten/sui/transactions";
import Decimal from "decimal.js";
import { MmtSDK } from "@mmt-finance/clmm-sdk";
import BN from "bn.js";
import { ExtendedPoolWithApr } from "@mmt-finance/clmm-sdk/dist/types";

export interface SdkConfig {
  suiClient: SuiClient
  mmtClmmSDK: MmtSDK
}

export interface CalculateAmountParams {
  vault_id: string
  is_amount_a: boolean
  input_amount: string
  slippage: number
  side: InputType
}

export interface CalculateAmountResult {
  request_id?: string
  side: InputType
  original_input_amount: string
  amount_a: string
  amount_b: string
  amount_limit_a: string
  amount_limit_b: string
  ft_amount: string
  is_amount_a: boolean
  swap_result?: SwapAmountResult
  partner?: string
}

export type SwapAmountResult = {
  swap_in_amount: string
  swap_out_amount: string
  a2b: boolean
  is_exceed: boolean
  sui_stake_protocol: SuiStakeProtocol
  after_sqrt_price?: string
  route_obj?: any
}

export enum SuiStakeProtocol {
  Mmt = 'Mmt',
  Haedal = 'Haedal',
  Volo = 'Volo',
  Aftermath = 'aftermath',
}

export enum InputType {
  Both = 'both',
  OneSide = 'oneSide',
}

export interface Vault {
  id: string
  clmm_pool_id: string
  free_balance_a: string
  free_balance_b: string
  fee_a: string
  fee_b: string
  seed_balance: string
  upper_price_scalling: string
  lower_price_scalling: string
  upper_price_scalling_dec: Decimal
  lower_price_scalling_dec: Decimal
  lower_trigger_price: string,
  upper_trigger_price: string,
  upper_trigger_price_scalling_dec: Decimal
  lower_trigger_price_scalling_dec: Decimal
  last_rebalance_sqrt_price: string
  last_rebalance_sqrt_price_dec: Decimal
  last_rebalance_time: string
  deposit_limit: string
  free_threshold_a: string
  free_threshold_b: string
  lock_threshold_a: string
  lock_threshold_b: string,

  slippage_up: string,
  slippage_down: string,
  slippage_up_dec: Decimal,
  slippage_down_dec: Decimal,
  // swap and reward fee percentage
  fee_val: string,
  // withdraw fee percentage
  withdraw_fee_val: string,
  // decimals of vault asset TokenA and TokenB
  decimals_a: string,
  decimals_b: string,
  coin_type_a: string
  coin_type_b: string
  lp_token_type: string
  config_type: string
  is_lock: boolean
  is_deposit_enabled: boolean
  pool_sqrt_price: string

  liquidity: string
  total_supply: string
  position: Position
  pool: ExtendedPoolWithApr
}

export interface VaultWithInfo extends Vault {
  vault_info: UserVaultInfo
}

export interface VaultBalance {
  vault_id: string,
  clmm_pool_id: string,
  owner: string,
  lp_token_type: string,
  lp_token_balance: string,
  lp_token_decimals: number,
  liquidity: string,
  tick_lower_index: number,
  tick_upper_index: number,
  amount_a: string,
  amount_b: string,
  coin_type_a: string,
  coin_type_b: string,
  decimal_a: string,
  decimal_b: string,
}

export interface UserVaultInfo {
  vaultName: string;
  aliasName: string;
  vaultAddress: string;
  vaultId: string;
  poolId: string;
  sourceProtocol: string;
  sourceProtocolUrl: string;
  sourceProtocolLogoUrl: string;
  apy: string;
  apyRateChange: string;
  vaultType: string;
  vaultLogo: string;
  vaultPrice: string;
  vaultPriceRateChange: string;
  earningRateChange: string;
  decimal: string;
  leftCoinType: string;
  leftCoinName: string;
  leftCoinLogo: string;
  leftTokenAmount: string;
  leftCoinPrice: string;
  leftCoinDecimal: string;
  leftSwapUrl: string;
  rightCoinType: string;
  rightCoinName: string;
  rightCoinLogo: string;
  rightTokenAmount: string;
  rightCoinPrice: string;
  rightCoinDecimal: string;
  rightSwapUrl: string;
  vaultOverview: string;
  deploymentUnix: string;
  maturity: string;
  fee: string;
  cardShowTagList: string[];
  cumulativeEarning: string;
  lastHarvestUnix: string;
  tradeStatus: string;
  vaultContract: string;
  vaultContractVersionList: string[];
  vaultGroup: string;
  vaultBalanceCap: string;
  vaultSupply: string;
  points: string;
  boost: string;
  tvl: string;
  earnings: string;
}

export interface UserVaultInfoResponse {
  count: number;
  data: UserVaultInfo[];
  msg: string;
  page: {
    pageIndex: number;
    pageSize: number;
  };
}

export interface VaultHolder {
  address: string;
  usdValue: number;
}

export interface VaultHoldersResponse {
  data: {
    [vaultId: string]: VaultHolder[];
  };
  msg: string;
}

export interface YtHoldDetail {
  marketStateId: string;
  usdValue: number;
  ytAmount: number;
}

export interface YtHoldResponse {
  data: {
    [vaultId: string]: YtHoldDetail;
  };
  msg: string;
}

export interface Position {
  id: string
  pool_id: string
  name: string
  liquidity: string
  clmm_position_id: string
  clmm_pool_id: string
  tick_lower_index: number
  tick_upper_index: number
  type_x: string
  type_y: string
  price_lower_tick_dec: Decimal
  price_upper_tick_dec: Decimal
}

export interface LiquidityInput {
  /**
   * The amount of coin A.
   */
  coin_amount_a: string

  /**
   * The amount of coin B.
   */
  coin_amount_b: string

  /**
   * The maximum amount of token A.
   */
  coin_amount_limit_a: string

  /**
   * The maximum amount of token B.
   */
  coin_amount_limit_b: string

  /**
   * The liquidity amount.
   */
  liquidity_amount: string

  /**
   * Whether to fix the amount of token A.
   */
  is_amount_a: boolean
}

export type CoinAmounts = {
  coin_amount_a: string
  coin_amount_b: string
}

export type DepositParams = {
  vault_id: string
  coin_object_a?: TransactionObjectArgument // If coin_object_a is provided, use coin_object_a. Please ensure coin_object is greater than or equal to amount_a
  coin_object_b?: TransactionObjectArgument // If coin_object_b is provided, use coin_object_b. Please ensure coin_object is greater than or equal to amount_b
  slippage: number
  deposit_result: CalculateAmountResult
  return_coin?: boolean
}

export type WithdrawParams = {
  vault_id: string
  ft_amount: string
  slippage: number
  return_coin?: boolean
}

export type CalculateRemoveAmountParams = {
  vault_id: string
  is_amount_a: boolean
  is_ft_input: boolean
  input_amount: string
  max_ft_amount: string
  slippage: number
  side: InputType
  request_id?: string
}

export type CalculateRemoveAmountResult = {
  request_id?: string
  side: InputType
  amount_a: string
  amount_b: string
  amount_limit_a: string
  amount_limit_b: string
  burn_ft_amount: string
  swap_result?: SwapAmountResult
}

/**
 * Represents a paginated data page with optional cursor and limit.
 */
export type DataPage<T> = {
  data: T[]
  next_cursor?: any
  has_next_page: boolean
}

/**
 * Represents query parameters for pagination.
 */
export type PageQuery = {
  cursor?: any
  limit?: number | null
}

/**
 * Represents arguments for pagination, with options for fetching all data or using PageQuery.
 */
export type PaginationArgs = 'all' | PageQuery

/**
 * Represents a SUI struct tag.
 */
export type SuiStructTag = {
  /**
   * The full address of the struct.
   */
  full_address: string

  /**
   * The source address of the struct.
   */
  source_address: string

  /**
   * The address of the struct.
   */
  address: string

  /**
   * The module to which the struct belongs.
   */
  module: string

  /**
   * The name of the struct.
   */
  name: string

  /**
   * An array of type arguments (SUI addresses) for the struct.
   */
  type_arguments: string[]
}

/**
 * Represents a coin asset with address, object ID, and balance information.
 */
export type CoinAsset = {
  /**
   * The address type of the coin asset.
   */
  coin_type: string

  /**
   * The object identifier of the coin asset.
   */
  coin_object_id: string

  /**
   * The balance amount of the coin asset.
   */
  balance: bigint
}

export type BuildCoinResult = {
  target_coin: TransactionObjectArgument
  selected_coins: string[]
  remain_coins: CoinAsset[]
  is_mint_zero_coin: boolean
  target_coin_amount: string
  original_spited_coin?: TransactionObjectArgument
}

export interface PreSwapLpChangeParams {
  poolID: string;
  ticklower: number;
  tickUpper: number;
  deltaLiquidity: number;
}

export interface FindRouterParams {
  from: string;
  target: string;
  amount: BN;
  byAmountIn: boolean;
  depth?: number;
  splitAlgorithm?: string;
  splitFactor?: number;
  splitCount?: number;
  providers?: string[];
  liquidityChanges?: PreSwapLpChangeParams[];
}

export type WithdrawBothParams = {
  vault_id: string
  ft_amount: string
  slippage: number
  return_coin?: boolean
}

export type WithdrawOneSideParams = {
  vault_id: string
  is_amount_a: boolean
  is_ft_input: boolean
  input_amount: string
  max_ft_amount: string
  slippage: number
  partner?: string
  return_coin?: boolean
  primary_coin_inputs?: TransactionObjectArgument
}

export interface CoinMetadata {
  decimals: number;
}

export interface CoinBalance {
  totalBalance: string;
};
